import { execute as executeCommand } from '../commandExecutor';
import { dispatchAction, getTenantSipPagingMyZones } from '../../api/backendApi';
import { HOME_ACTION_ASK_SB } from '../../shared/buttonEditor/constants';
import { flowDisabledUserMessage, isFlowRuntimeEnabled } from '../../utils/flowEnabled';
import { isFailedExecutionResult } from '../../utils/executionStatusDisplay';

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Returned types:
 *   'paging' | 'sipPaging' | 'imageDisplay' | 'collaboration' | 'contact_list' | 'phone' |
 *   'reminders' | 'emailRem' | 'askSb' | 'action' | 'openScreenSaver' | 'flow' | 'settings' | 'none' | 'error'
 */
export const getActionIntent = (icon) => {
  const actionType =
    icon.actionType === 'external'
      ? 'none'
      : icon.actionType || (icon.flowId ? 'flow' : 'none');

  if (actionType === 'paging' && icon.pagingZoneId) {
    return { type: 'paging', icon };
  }
  if (actionType === 'sipPaging' && icon.sipPagingZoneId) {
    return { type: 'sipPaging', icon };
  }
  if (actionType === 'imageDisplay' && icon.imageDisplayActionId) {
    return { type: 'imageDisplay', icon };
  }
  if (actionType === 'imageDisplay' && !icon.imageDisplayActionId) {
    return { type: 'error', message: 'Image Display is not configured for this button' };
  }
  if (actionType === 'collaboration') {
    if (icon.collaborationWorkspaceId) {
      return { type: 'collaboration', icon };
    }
    return { type: 'error', message: 'Collaboration button has no workspace configured. Please edit the button.' };
  }
  if (actionType === 'contact_list') {
    if (icon.contactListId) {
      return { type: 'contact_list', icon };
    }
    return { type: 'error', message: 'Contact List button has no list configured. Please edit the button.' };
  }
  if (actionType === 'phone') {
    return { type: 'phone', icon };
  }
  if (actionType === 'reminders') {
    return { type: 'reminders', icon };
  }
  if (actionType === 'emailRem') {
    return { type: 'emailRem', icon };
  }
  if (actionType === HOME_ACTION_ASK_SB) {
    return { type: 'askSb', icon };
  }
  if (actionType === 'action') {
    if (icon.actionId) {
      return { type: 'action', icon };
    }
    return {
      type: 'error',
      message: 'This button has no action assigned. Please edit the button and select an action.',
    };
  }
  if (actionType === 'openScreenSaver') {
    return { type: 'openScreenSaver' };
  }
  if (!icon.flowId) {
    if (icon.id === 'settings') return { type: 'settings' };
    return { type: 'none' };
  }
  if (UUID_LIKE.test(String(icon.flowId))) {
    return {
      type: 'error',
      message:
        'This button is misconfigured (invalid Flow ID). Please edit the button and re-select the Flow.',
      debug: { iconId: icon.id, flowId: icon.flowId },
    };
  }
  return { type: 'flow', icon };
};

/**
 * Build the flow execution payload for a given icon + tenantId.
 */
export const buildFlowPayload = (icon, tenantId) => {
  const traceId =
    (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
    `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return {
    flowId: icon.flowId,
    tenantId,
    traceId,
    correlationId: traceId,
    source: 'home_launcher',
    trigger: 'launcher_button',
    launcherButtonId: icon.id,
    launcherButtonLabel: icon.label,
  };
};

/**
 * Extract all incidentIds from a flow execution's actionResults array.
 */
export const extractIncidentIds = (actionResults) => {
  const ids = [];
  (actionResults || []).forEach((r) => {
    const d = r?.details || {};
    if (d?.incidentId) ids.push(d.incidentId);
    if (Array.isArray(d?.incidentIds)) ids.push(...d.incidentIds);
  });
  return Array.from(new Set(ids.filter(Boolean)));
};

/**
 * Execute a flow action end-to-end.
 * Returns { incidentIds, result }.
 * Throws on error.
 */
/**
 * Execute one Event System action from a Home button.
 * Returns { result, traceId }.
 * Throws on error.
 */
export const executeDirectAction = async (icon, tenantId) => {
  if (!icon?.actionId) {
    throw new Error('This button has no action assigned.');
  }
  if (!tenantId) {
    throw new Error('Tenant is required');
  }
  const traceId =
    (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
    `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const resp = await dispatchAction({
    tenantId,
    actionId: icon.actionId,
    context: {
      source: 'home_launcher',
      trigger: 'launcher_button',
      launcherButtonId: icon.id,
      launcherButtonLabel: icon.label,
      traceId,
      correlationId: traceId,
    },
  });
  if (!resp?.success) {
    throw new Error(resp?.error || resp?.message || 'Failed to run action');
  }
  return { result: resp, traceId };
};

export const executeFlowAction = async (icon, tenantId) => {
  const payload = buildFlowPayload(icon, tenantId);
  console.info('[LauncherAction] Triggering flow', {
    traceId: payload.traceId,
    flowId: icon.flowId,
    tenantId,
    iconId: icon.id,
    label: icon.label,
  });
  const res = await executeCommand(icon.flowId, payload);
  if (isFailedExecutionResult(res)) {
    throw new Error(flowDisabledUserMessage(null, res) || res.message || res.error || 'Failed to trigger flow');
  }
  const incidentIds = extractIncidentIds(res?.details?.actionResults);
  return { incidentIds, result: res };
};

export function isFlowLauncherAction(icon) {
  if (!icon) return false;
  const actionType =
    icon.actionType === 'external'
      ? 'none'
      : icon.actionType || (icon.flowId ? 'flow' : 'none');
  return actionType === 'flow' && Boolean(icon.flowId);
}

export function launcherIconVisible(icon, { editMode = false, flowById = null, keepAssigned = false } = {}) {
  if (editMode) return true;
  if (!isFlowLauncherAction(icon)) return true;
  const map = flowById instanceof Map ? flowById : null;
  const flow = map
    ? map.get(icon.flowId) || map.get(String(icon.flowId))
    : flowById?.[icon.flowId] || flowById?.[String(icon.flowId)];
  if (!flow) {
    // Assigned Home Designer buttons stay visible even if the Flow catalog
    // has not loaded that id yet. Hide only unmatched legacy launcher tiles.
    if (keepAssigned) return true;
    if (map && map.size > 0) return false;
    return true;
  }
  return isFlowRuntimeEnabled(flow);
}

export function indexFlowsById(flows = []) {
  const map = new Map();
  (Array.isArray(flows) ? flows : []).forEach((flow) => {
    if (!flow || typeof flow !== 'object') return;
    const id = flow.flowId || flow.id;
    if (id) map.set(String(id), flow);
  });
  return map;
}

/**
 * Resolve the SIP URI for an icon's sipPagingZoneId.
 * Returns { sipUri } or throws with a user-friendly message.
 */
export const resolveSipPagingZone = async (tenantId, icon) => {
  const zones = await getTenantSipPagingMyZones(tenantId);
  const zone = zones.find((z) => z.id === icon.sipPagingZoneId);
  if (!zone) throw new Error('SIP paging zone not found or not accessible');
  if (!zone.sipUri) throw new Error('SIP paging zone has no SIP URI configured');
  return { sipUri: zone.sipUri };
};
